import { useState, useEffect, useCallback } from 'react';
import {
  apiClient,
  HealthResponse,
  StatusResponse,
  MetricsResponse,
  AnalysisRequest,
  AnalysisResponse,
  AIRoutingRequest,
  AIRoutingResponse,
  SuggestionRequest,
  SuggestionsResponse,
  ApiResponse
} from '@/services/api';
import { APIUtils } from '@/utils/api-utils';

// Generic hook for API calls
export function useAPICall<T>(
  apiCall: () => Promise<ApiResponse<T>>,
  dependencies: any[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiCall();

      if (APIUtils.isSuccess(response)) {
        setData(response.data);
      } else {
        setError(APIUtils.getErrorMessage(response));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    execute();
  }, [execute]);

  return { data, loading, error, refetch: execute };
}

// Health check hook
export function useHealth() {
  return useAPICall(() => apiClient.getHealth(), []);
}

// Service status hook
export function useStatus() {
  return useAPICall(() => apiClient.getStatus(), []);
}

// Metrics hook with auto-refresh
export function useMetrics(refreshInterval: number = 30000) {
  const { data, loading, error, refetch } = useAPICall(
    () => apiClient.getMetrics(),
    []
  );

  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(refetch, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, refetch]);

  return { data, loading, error, refetch };
}

// Project analysis hook
export function useProjectAnalysis() {
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const analyze = useCallback(async (request: AnalysisRequest) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.analyzeProject(request);

      if (APIUtils.isSuccess(response)) {
        setAnalysis(response.data);
      } else {
        setError(APIUtils.getErrorMessage(response));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  return { analysis, loading, error, analyze };
}

// AI routing hook
export function useAIRouting() {
  const [routing, setRouting] = useState<AIRoutingResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const routeModel = useCallback(async (request: AIRoutingRequest) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.routeAIModel(request);

      if (APIUtils.isSuccess(response)) {
        setRouting(response.data);
      } else {
        setError(APIUtils.getErrorMessage(response));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  return { routing, loading, error, routeModel };
}

// Suggestions hook
export function useSuggestions() {
  const [suggestions, setSuggestions] = useState<SuggestionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const getSuggestions = useCallback(async (request: SuggestionRequest) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getSuggestions(request);

      if (APIUtils.isSuccess(response)) {
        setSuggestions(response.data);
      } else {
        setError(APIUtils.getErrorMessage(response));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  return { suggestions, loading, error, getSuggestions };
}

// API availability hook
export function useAPIAvailability() {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [checking, setChecking] = useState<boolean>(false);

  const checkAvailability = useCallback(async () => {
    setChecking(true);
    try {
      const isAvailable = await apiClient.checkAvailability();
      setAvailable(isAvailable);
    } catch (error) {
      setAvailable(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    checkAvailability();

    // Check every 30 seconds
    const interval = setInterval(checkAvailability, 30000);
    return () => clearInterval(interval);
  }, [checkAvailability]);

  return { available, checking, checkAvailability };
}

// API status summary hook
export function useAPIStatusSummary() {
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const getStatusSummary = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const statusSummary = await apiClient.getStatusSummary();
      setSummary(statusSummary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getStatusSummary();

    // Refresh every 60 seconds
    const interval = setInterval(getStatusSummary, 60000);
    return () => clearInterval(interval);
  }, [getStatusSummary]);

  return { summary, loading, error, refetch: getStatusSummary };
}

// Local storage hook for persisting form data
export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue] as const;
}